import { FormInstance } from 'antd';
import { Form } from '@common/components';
import { Store } from 'antd/lib/form/interface';
import React from 'react';

import { useMutation } from '@common/hooks';
import { ComplianceItemUploadInput } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemType } from '@onboarding/graphql';
import {
  CreateDocumentMutation,
  UpdateDocumentMutation,
} from 'works/legacy/graphql/document';
import { formatToYearMonthDay } from '@common/services/datetime';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemType;
  refetchQueries: string[];
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemAddAttachmentFormIdDoc({
  form,
  complianceItem,
  refetchQueries,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update document. Please try again.');

  const [updateDocument, { loading: formSubmitting }] = useMutation(
    UpdateDocumentMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateDocument.errors) {
          addErrorsToForm(form, response.updateDocument.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'Document has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const [createDocument] = useMutation(CreateDocumentMutation, {
    refetchQueries,
    onCompleted: (response) => {
      if (response?.createDocument.errors) {
        addErrorsToForm(form, response.createDocument.errors);
        errorMessage();
        onError?.();
      } else {
        onSuccess?.();
        createMessage('success', 'Document has been successfully created.');
      }
    },
    onError: () => {
      errorMessage();
      onError?.();
    },
  });

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    if (!detailItem) {
      const initialDocType = complianceItem.documentTemplate?.key;
      const defaultTitle = `${
        complianceItem.documentTemplateSubtype || ''
      } ${complianceItem.documentTemplate?.name}`;

      createDocument({
        variables: {
          worksListItemId: complianceItem.id,
          worksProfileId:
            complianceItem.worksList.staffingRequestAssignment.worksProfile.id,
          input: {
            title: defaultTitle,
            expiresOn: formatToYearMonthDay(values.expiresOn),
            completedOn: formatToYearMonthDay(values.completedOn),
            docType: initialDocType,
          },
          files: values.files ? values.files.fileList : null,
        },
      });

      return;
    }

    updateDocument({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
        files: values.files?.fileList ?? [],
      },
    });
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <ComplianceItemInputExpirationDate
        expirationType={complianceItem.expirationType}
      />

      <ComplianceItemUploadInput multiple required />
    </Form>
  );
}
